import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { Protected } from "@/components/AppShell";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, X, DownloadSimple, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Responses() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filters, setFilters] = useState({ q: "", template_id: "all", date_from: "", date_to: "" });
  const [refreshing, setRefreshing] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.template_id && filters.template_id !== "all") params.template_id = filters.template_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/responses", { params });
      setItems(data);
    } catch (err) {
      if (!silent) toast.error("Impossible de rafraîchir la liste");
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      // Include soft-deleted templates so a response filed against a removed
      // questionnaire still surfaces in the filter (with a "(supprimé)" tag).
      const { data } = await api.get("/templates", { params: { include_deleted: true } });
      // Cabinet-owned templates only: the validated public library is not
      // filterable here — the filter is scoped to what this cabinet actually
      // authored/received answers for.
      setTemplates(
        data
          .filter((t) => !t.is_validated)
          .sort((a, b) => (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" }))
      );
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load({ silent: true }), 250);
    return () => clearTimeout(t);
  }, [filters]);

  const reset = () => setFilters({ q: "", template_id: "all", date_from: "", date_to: "" });
  const hasFilters = filters.q || (filters.template_id && filters.template_id !== "all") || filters.date_from || filters.date_to;

  const exportXlsx = async () => {
    const params = new URLSearchParams();
    if (filters.q) params.set("q", filters.q);
    if (filters.template_id && filters.template_id !== "all") params.set("template_id", filters.template_id);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    try {
      const res = await fetch(`${API}/export/responses.xlsx?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reponses-questiomed-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  return (
    <Protected>
      <AppShell
        title="Réponses"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => load()}
              disabled={refreshing}
              className="rounded-md font-semibold group disabled:opacity-50 disabled:cursor-wait"
              data-testid="refresh-responses-btn"
              title="Rafraîchir"
            >
              <ArrowClockwise size={14} weight="bold" className={`mr-1.5 ${refreshing ? "animate-spin" : "transition-transform group-hover:rotate-45"}`} />
              Rafraîchir
            </Button>
            <Button
              onClick={exportXlsx}
              disabled={items.length === 0}
              variant="outline"
              className="rounded-md border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
              data-testid="export-xlsx-btn"
            >
              <DownloadSimple size={16} className="mr-1.5" /> Exporter Excel
            </Button>
          </div>
        }
      >
        <div className="bg-white border border-[#E1E9F0] rounded-2xl p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Rechercher</Label>
              <div className="relative mt-2">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5F758F]" />
                <Input
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  placeholder="Patient, email, questionnaire…"
                  className="pl-9 bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5]"
                  data-testid="responses-search"
                />
              </div>
            </div>
            <div className="md:col-span-4">
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Questionnaire</Label>
              <Select value={filters.template_id} onValueChange={(v) => setFilters({ ...filters, template_id: v })}>
                <SelectTrigger className="mt-2 bg-white border-[#E1E9F0]" data-testid="filter-template">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.template_id} value={t.template_id}>
                      <span className="inline-flex items-center gap-2 flex-wrap">
                        <span>{t.title}</span>
                        {t.is_deleted && (
                          <span className="inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] tracking-[0.14em] uppercase font-semibold italic">Supprimé</span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Du</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="mt-2 bg-white border-[#E1E9F0]"
                data-testid="filter-date-from"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Au</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="mt-2 bg-white border-[#E1E9F0]"
                data-testid="filter-date-to"
              />
            </div>
          </div>
          {hasFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-[#5F758F]">{items.length} résultat{items.length > 1 ? "s" : ""}</p>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="reset-filters">
                <X size={14} className="mr-1.5" /> Réinitialiser
              </Button>
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-16 text-center">
            <p className="text-[#5F758F]">Aucune réponse trouvée.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {items.map((r) => {
              const subTone = (interp) => {
                const s = (interp || "").toLowerCase();
                if (/sévère|severe|important|critique/.test(s)) return "crit";
                if (/modér|moder|léger|leger/.test(s)) return "warn";
                return "normal";
              };
              const globalTone = subTone(r.score?.interpretation);
              return (
                <Link
                  key={r.response_id}
                  to={`/responses/${r.response_id}`}
                  className="bg-white border border-[#E1E9F0] rounded-[14px] px-6 py-5 grid grid-cols-[1fr_auto] gap-6 items-start transition-all hover:border-[#3A8FA5] hover:shadow-[0_2px_8px_rgba(58,143,165,0.08)]"
                  data-testid={`resp-${r.response_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-[16px] font-semibold text-[#1E3A5F] mb-0.5">{r.patient_name}</p>
                    <p className="text-[14px] text-[#5F758F]">
                      {r.template_title}
                      {r.template_is_deleted && (
                        <span className="ml-2 inline-flex items-center rounded-[4px] bg-[#FFE0DC] text-[#8B2A1E] border border-[#EEC1BB] px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-semibold italic" data-testid={`resp-deleted-${r.response_id}`}>
                          Supprimé
                        </span>
                      )}
                      <span className="mx-1.5">·</span>
                      {new Date(r.submitted_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  {r.score && (
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className="flex items-center gap-3">
                        {r.score.interpretation && (
                          <span
                            className={`text-[12px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap border ${
                              globalTone === "crit"
                                ? "bg-[#FFE0DC] text-[#8B2A1E] border-[#EEC1BB]"
                                : globalTone === "warn"
                                  ? "bg-[#FFF3D6] text-[#B76A00] border-[#F0DDA8]"
                                  : "bg-[#E6F1F5] text-[#3A8FA5] border-[#C7DEE6]"
                            }`}
                            data-testid={`resp-interp-${r.response_id}`}
                          >
                            {r.score.interpretation}
                          </span>
                        )}
                        <div className="text-right">
                          <p className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold mb-1">Score</p>
                          <p className="text-[24px] font-bold text-[#1E3A5F] leading-none tracking-tight tabular-nums">
                            {r.score.percentage != null ? `${r.score.percentage} %` : `${r.score.total} / ${r.score.max}`}
                          </p>
                        </div>
                      </div>
                      {Array.isArray(r.score.subscores) && r.score.subscores.length > 0 && (
                        <div className="flex flex-wrap justify-end gap-1.5 max-w-[380px]" data-testid={`resp-subs-${r.response_id}`}>
                          {r.score.subscores.map((s) => {
                            const tone = subTone(s.interpretation);
                            const cls = tone === "crit"
                              ? "bg-[#FFE0DC] border-[#EEC1BB] text-[#8B2A1E]"
                              : tone === "warn"
                                ? "bg-[#FFF3D6] border-[#F0DDA8] text-[#1E3A5F]"
                                : "bg-[#EEF3F8] border-[#E1E9F0] text-[#1E3A5F]";
                            const iCls = tone === "crit" ? "text-[#8B2A1E] font-semibold" : "text-[#D45A3F]";
                            return (
                              <span key={s.key} className={`inline-flex items-baseline gap-1.5 px-3 py-1 rounded-full text-[12px] font-medium border whitespace-nowrap ${cls}`}>
                                <span className={tone === "normal" ? "text-[#5F758F]" : ""}>{s.label}</span>
                                <span className="font-bold tabular-nums">{s.total}/{s.max}</span>
                                {s.interpretation && <span className={`text-[11px] ${iCls}`}>· {s.interpretation}</span>}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </AppShell>
    </Protected>
  );
}
